import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import type { CarouselAPI } from '@elfsight/lib-carousel';
import {
  CarouselItem,
  LazyCarousel,
  MIN_BULLETS_COUNT
} from '@elfsight/lib-carousel';
import type { Swiper } from '@elfsight/reexported-swiper';
import { useIsMobile } from '@elfsight/widget-utils';
import { concat } from 'es-toolkit/compat';
import type { DataSourceProps, MappingFunction } from '../types';
import {
  computeClassNames,
  createCarouselAPI,
  generateDataChunks,
  transformDataSource,
  useColumns,
  useDelayedLoopToggle,
  useLayoutData
} from '../utils';

export interface SliderLayoutProps {
  dataSource: DataSourceProps;
  columnsBasis?: number;
  scrollMode: 'slide' | 'item';
  autoplay: boolean;
  autoplayPauseOnHover: boolean;
  autoplayInterval: number;
  animationType: 'slide' | 'coverflow' | 'fade';
  animationSpeed: number;
  pagination: boolean;
  arrowsEnable: boolean;
  arrowsShowOnHover: boolean;
  arrowsOnMobileEnable: boolean;
  swipeDesktopEnable: boolean;
  swipeMobileEnable: boolean;
  rtl?: boolean;
  arrowBackgroundColor?: string;
  arrowColor?: string;
  arrowBackgroundColorOnHover?: string;
  arrowColorOnHover?: string;
  arrowSize?: number;
  arrowMargin?: number;
  paginationColor?: string;
  className?: string;
  reportSlideChangeInstantly?: boolean;
  renderItem: MappingFunction;
  renderLoader: MappingFunction;
  onVisibleItemsChange?: (visibleItemsIndexes: number[]) => void;
  containerHorizontalPadding?: number;
}

const COLUMNS_COUNT = 1;

export const SliderLayout = forwardRef<CarouselAPI, SliderLayoutProps>(
  function SliderLayout(
    {
      dataSource,
      scrollMode,
      autoplay,
      autoplayPauseOnHover,
      autoplayInterval,
      animationType,
      animationSpeed,
      pagination,
      arrowsEnable,
      arrowsOnMobileEnable,
      swipeDesktopEnable,
      swipeMobileEnable,
      rtl,
      arrowsShowOnHover,
      arrowBackgroundColor,
      arrowColor,
      arrowColorOnHover,
      arrowBackgroundColorOnHover,
      arrowSize,
      arrowMargin,
      paginationColor,
      className,
      reportSlideChangeInstantly,
      renderItem,
      renderLoader,
      onVisibleItemsChange,
      containerHorizontalPadding
    },
    forwardingRef
  ) {
    const isDataStatic = Array.isArray(dataSource);
    const dataSourceFormat = transformDataSource(dataSource);

    const {
      data: initialData,
      hasNext: hasNextFetch,
      next: handleNextFetch
    } = dataSourceFormat;

    const containerRef = useRef<HTMLDivElement | null>(null);

    const carouselRefAPI = useRef<CarouselAPI>(null);

    const { windowWidth } = useColumns({});

    const isMobile = useIsMobile();

    const swipeEnabled = useMemo(
      () => (isMobile ? swipeMobileEnable : swipeDesktopEnable),
      [isMobile, swipeDesktopEnable, swipeMobileEnable]
    );

    const arrowsEnabled = useMemo(
      () => (isMobile ? arrowsOnMobileEnable : arrowsEnable),
      [isMobile, arrowsEnable, arrowsOnMobileEnable]
    );
    const showArrowsControlOnHover = useMemo(
      () => (isMobile ? false : arrowsShowOnHover),
      [isMobile, arrowsShowOnHover]
    );

    const { data, hasNext, loadNext } = useLayoutData({
      data: initialData,
      itemsCountPerPage: Math.max(COLUMNS_COUNT * 2, MIN_BULLETS_COUNT),
      hasNextFetch,
      handleNextFetch,
      withPreloadData: true
    });

    const shouldLoop =
      hasNext !== undefined && !hasNext && data.length > COLUMNS_COUNT;
    const isLoopActive = useDelayedLoopToggle(shouldLoop, animationSpeed);

    const [currentChunk, setChunk] = useState(0);
    const [dataChunks, setDataChunks] = useState(
      generateDataChunks(
        data,
        COLUMNS_COUNT,
        currentChunk,
        renderItem,
        renderLoader,
        isLoopActive
      )
    );

    const dataForRender = concat(...dataChunks);

    const handleSlideChange = (swiper: Swiper) => {
      const slideIndex = swiper.realIndex;
      const slidesCount = swiper.slides.length;

      if (slidesCount > 1 && slideIndex === slidesCount - 2) {
        loadNext();
      }

      const chunk = Math.round(slideIndex / COLUMNS_COUNT);
      if (chunk !== currentChunk) {
        setChunk(chunk);
      }
    };

    useEffect(() => {
      const switchAutoplayHandler: IntersectionObserverCallback = function (
        entries
      ) {
        const carouselApi = carouselRefAPI.current;

        if (!carouselApi) {
          return;
        }

        const isSliderVisible = entries[0]?.isIntersecting;
        const isSliderRunning = carouselApi.autoplayIsRunning();

        if (autoplay && isSliderRunning && !isSliderVisible) {
          carouselApi.autoplayStop();
        }
        if (autoplay && !isSliderRunning && isSliderVisible) {
          carouselApi.autoplayStart();
        }
      };
      const observer = new IntersectionObserver(switchAutoplayHandler);
      const sliderWrapper = containerRef.current;
      if (sliderWrapper) {
        observer.observe(sliderWrapper);
      }

      return () => {
        if (sliderWrapper) {
          observer.disconnect();
        }
      };
    }, [autoplay, containerRef, carouselRefAPI]);

    const updateAutoHeight = useCallback(() => {
      if (carouselRefAPI?.current) {
        carouselRefAPI?.current.updateAutoHeight();
      }
    }, []);

    useEffect(() => {
      setDataChunks(
        generateDataChunks(
          data,
          COLUMNS_COUNT,
          currentChunk,
          renderItem,
          renderLoader,
          isLoopActive
        )
      );
      updateAutoHeight();
    }, [data, currentChunk, renderItem, renderLoader, isLoopActive]);

    const resetSliderSlide = useCallback(() => {
      setChunk(0);
      if (carouselRefAPI?.current) {
        carouselRefAPI?.current.slideTo(0);
      }
    }, []);

    useImperativeHandle(
      forwardingRef,
      () =>
        createCarouselAPI(carouselRefAPI, { resetLayout: resetSliderSlide }),
      [resetSliderSlide]
    );

    useEffect(() => {
      resetSliderSlide();
    }, [windowWidth, animationType]);

    useEffect(() => {
      if (isDataStatic) {
        resetSliderSlide();
      }
    }, [initialData]);

    return (
      <CarouselWrapper
        ref={containerRef}
        className={computeClassNames('es-slider-layout-container', className)}
      >
        <LazyCarousel
          ref={carouselRefAPI}
          withAutoplay={autoplay}
          autoplayDelay={autoplayInterval}
          slideBy={scrollMode}
          loop={isLoopActive}
          itemsPerSlide={COLUMNS_COUNT}
          withArrowControl={arrowsEnabled}
          showArrowsControlOnHover={showArrowsControlOnHover}
          withPaginationControl={pagination}
          rtl={rtl}
          pauseOnHover={autoplayPauseOnHover}
          allowTouchMove={swipeEnabled}
          effect={animationType}
          animationSpeed={animationSpeed}
          onSlideChange={handleSlideChange}
          arrowControlBackgroundColor={arrowBackgroundColor}
          arrowControlBackgroundColorOnHover={arrowBackgroundColorOnHover}
          arrowControlIconColor={arrowColor}
          arrowControlIconColorOnHover={arrowColorOnHover}
          arrowControlSize={arrowSize}
          arrowControlMargin={arrowMargin}
          paginationControlBackgroundColor={paginationColor}
          className="es-slider-layout"
          arrowControlPosition="adaptive"
          onVisibleItemsChange={onVisibleItemsChange}
          reportSlideChangeInstantly={reportSlideChangeInstantly}
          containerHorizontalPadding={containerHorizontalPadding}
        >
          {dataForRender?.map((item, index) => (
            <CarouselItem key={index} className="es-slider-layout-item">
              {item}
            </CarouselItem>
          ))}
        </LazyCarousel>
      </CarouselWrapper>
    );
  }
);

export default SliderLayout;

const CarouselWrapper = styled.div`
  max-width: 100%;
  /* Необходимо для исключения краша slider layout при width: 100%, если родительскому контейнеру задан display: flex */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
`;
